/* eslint-disable @gitlab/require-i18n-strings */
import isPlainObject from 'lodash-es/isPlainObject';

/**
 * @typedef {Object} MessageWidget
 * @property {(message: Object) => boolean} matchMessage - Claims a message for this
 *   widget. Must be pure and cheap: duo-ui calls it for every renderer against every
 *   message.
 * @property {Object} component - Vue SFC rendering the message.
 * @property {((message: Object, workingDirectory: string) => Object)|Object} [defaultProps] -
 *   Replaces (not merges with) the default `{ message, workingDirectory }` props.
 * @property {'inline'|'pinned'} [layout] - `pinned` renders at the conversation tail.
 * @property {boolean} [groupable] - `false` keeps a tool message out of tool-groups.
 */

const LAYOUTS = ['inline', 'pinned'];

export const messageWidgets = {
  key: 'messageWidgets',

  /**
   * Validates if a message widget is valid.
   *
   * @param {unknown} widget
   * @returns {string[]} One message per contract violation; empty when valid.
   */
  validate(widget) {
    if (!isPlainObject(widget)) {
      return ['must be an object'];
    }

    const { matchMessage, component, defaultProps, layout, groupable } = widget;
    const errors = [];

    if (typeof matchMessage !== 'function') {
      errors.push('`matchMessage` must be a function');
    }

    if (!isPlainObject(component)) {
      errors.push('`component` must be a Vue component');
    }

    if (
      defaultProps !== undefined &&
      typeof defaultProps !== 'function' &&
      !isPlainObject(defaultProps)
    ) {
      errors.push('`defaultProps` must be a function or an object');
    }

    if (layout !== undefined && !LAYOUTS.includes(layout)) {
      errors.push(`\`layout\` must be one of: ${LAYOUTS.join(', ')}`);
    }

    if (groupable !== undefined && typeof groupable !== 'boolean') {
      errors.push('`groupable` must be a boolean');
    }

    return errors;
  },

  /**
   * Flattens every plugin's widgets into the descriptor array duo-ui's MessageMap
   * consumes. Registration order is precedence order: duo-ui merges these ahead of
   * its built-ins and takes the first match.
   *
   * @param {import('../plugin_registry').DuoChatPlugin[]} plugins
   * @returns {MessageWidget[]}
   */
  resolve(plugins) {
    return plugins.flatMap(({ messageWidgets: widgets = [] }) => widgets);
  },
};
